import React from 'react'
import i18n from 'config/i18n'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { SubmissionState, PartiposterProfile } from 'state/submissions'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { PLATFORM_ENUM } from 'services/api/constants'
import { DropoutsTableRows, GridColDefProps } from './constants'
import { showDefaultIgPlatform } from 'components/utils/campaign'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'

export const getColumns = (
    searchablePlatforms: PLATFORM_ENUM[],
    canManageTag: boolean,
    followerRequirementsPlatform: PLATFORM_ENUM[],
    updateViewedRow?: (id: string) => void,
): GridColDefProps[] => {
    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return searchablePlatforms.includes(platform) || followerRequirementsPlatform.includes(platform)
    }
    const cols: GridColDefProps[] = [
        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
            ],
        },
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => {
                const handleUpdateViewed = () => {
                    if (updateViewedRow) {
                        updateViewedRow(props.row.uuid)
                    }
                }
                return <SocialMediaInfoCell {...props} onClick={handleUpdateViewed} />
            },
            subFields: [
                ...(showDefaultIgPlatform(searchablePlatforms, followerRequirementsPlatform)
                    ? [
                          {
                              field: FIELDS_TYPE.IG_FOLLOWERS,
                              headerName: i18n.t('submission.table.instagramFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.IG_USERNAME,
                              headerName: i18n.t('submission.table.instagramUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.FB)
                    ? [
                          {
                              field: FIELDS_TYPE.FB_FRIENDS,
                              headerName: i18n.t('submission.table.facebookFriendsLabel'),
                          },
                          {
                              field: FIELDS_TYPE.FB_USERNAME,
                              headerName: i18n.t('submission.table.facebookUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                    ? [
                          {
                              field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                              headerName: i18n.t('submission.table.tikTokFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TIKTOK_USERNAME,
                              headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                    ? [
                          {
                              field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                              headerName: i18n.t('submission.table.twitterFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TWITTER_USERNAME,
                              headerName: i18n.t('submission.table.twitterUsernameLabel'),
                          },
                      ]
                    : []),
            ],
        },
    ]
    return canManageTag
        ? cols.concat([
              {
                  field: 'partiposterTags',
                  headerName: i18n.t('submission.table.partiposterTagsLabel'),
                  width: 240,
                  minWidth: 200,
                  flex: 1,
                  renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                  sortable: false,
                  filterable: false,
              },
          ])
        : cols
}

export const getRows = (
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
): DropoutsTableRows[] => {
    return submissions.map((submission: SubmissionState) => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.IG)
        const tiktokProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TIKTOK)
        const fbProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.FB)
        const twitterProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TWITTER)
        return {
            id: submission.uuid,
            profile: {
                profile,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile],
            },
            partiposterTags: {
                profileData: profile,
                // Note: there's no `submission_uuid` property with the submission dropout object
                submissionUuid: submission.uuid,
                campaignUuid: campaignUuid,
            },
        }
    })
}
